import { useState, useRef } from "react";
import { useLanguage, SUPPORTED_LANGUAGES } from "@/context/LanguageContext";
import { Search, Upload, Download, Plus, Trash2, Check, X, Globe, FileJson, FileSpreadsheet, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const FLAG_EMOJIS = {
  US: "\uD83C\uDDFA\uD83C\uDDF8",
  IN: "\uD83C\uDDEE\uD83C\uDDF3",
  ES: "\uD83C\uDDEA\uD83C\uDDF8",
  FR: "\uD83C\uDDEB\uD83C\uDDF7",
  DE: "\uD83C\uDDE9\uD83C\uDDEA",
  JP: "\uD83C\uDDEF\uD83C\uDDF5",
  CN: "\uD83C\uDDE8\uD83C\uDDF3",
  SA: "\uD83C\uDDF8\uD83C\uDDE6",
  BR: "\uD83C\uDDE7\uD83C\uDDF7",
  KR: "\uD83C\uDDF0\uD83C\uDDF7",
};

const CATEGORY_ORDER = [
  "nav", "action", "label", "tenant", "industry", "org_type", "company_size", 
  "widget", "role", "status", "translation"
];

export default function TranslationManagement() {
  const {
    translations,
    updateTranslation,
    bulkUpdateTranslations,
    addTranslationKey,
    deleteTranslationKey,
    importTranslations,
    exportTranslations,
    getAllKeys,
    getTranslationStats,
    resetToDefaults,
    t
  } = useLanguage();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("fr");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [addKeyDialogOpen, setAddKeyDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const fileInputRef = useRef(null);

  const allKeys = getAllKeys();
  const stats = getTranslationStats(selectedLanguage);
  const enTranslations = translations.en || {};
  const langTranslations = translations[selectedLanguage] || {};

  // Group keys by category
  const getCategory = (key) => key.split(".")[0];
  
  const categories = [...new Set(allKeys.map(getCategory))].sort((a, b) => {
    const aIndex = CATEGORY_ORDER.indexOf(a);
    const bIndex = CATEGORY_ORDER.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // Filter keys
  const filteredKeys = allKeys.filter(key => {
    const matchesSearch = key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      enTranslations[key]?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      langTranslations[key]?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filterCategory === "all" || getCategory(key) === filterCategory;
    
    const isTranslated = !!langTranslations[key];
    const matchesStatus = filterStatus === "all" ||
      (filterStatus === "translated" && isTranslated) ||
      (filterStatus === "missing" && !isTranslated);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Group filtered keys by category for display
  const groupedKeys = filteredKeys.reduce((acc, key) => {
    const cat = getCategory(key);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(key);
    return acc;
  }, {});

  const handleAddKey = () => {
    if (!newKey || !newValue) return;
    addTranslationKey(newKey, newValue);
    toast.success(`Added translation key: ${newKey}`);
    setNewKey("");
    setNewValue("");
    setAddKeyDialogOpen(false);
  };

  const handleUpdateTranslation = (key) => {
    updateTranslation(selectedLanguage, key, editingValue);
    toast.success("Translation updated");
    setEditingKey(null);
    setEditingValue("");
  };

  const handleDeleteKey = (key) => {
    if (confirm(`Delete translation key "${key}" from all languages?`)) {
      deleteTranslationKey(key);
      toast.success(`Deleted: ${key}`);
    }
  };

  const handleExport = (format) => {
    const data = exportTranslations(selectedLanguage);
    let content, filename, type;
    
    if (format === "json") {
      content = JSON.stringify(data, null, 2);
      filename = `translations_${selectedLanguage}.json`;
      type = "application/json";
    } else {
      // CSV format
      const rows = [["key", "english", selectedLanguage]];
      allKeys.forEach(key => {
        rows.push([key, enTranslations[key] || "", data[key] || ""]);
      });
      content = rows.map(row => row.map(cell => `"${(cell || "").replace(/"/g, '""')}"`).join(",")).join("\n");
      filename = `translations_${selectedLanguage}.csv`;
      type = "text/csv";
    }
    
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${format.toUpperCase()} file`);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      
      if (file.name.endsWith(".json")) {
        const result = importTranslations(selectedLanguage, content);
        if (result.success) {
          toast.success(`Imported ${result.count} translations`);
        } else {
          toast.error(`Import failed: ${result.error}`);
        }
      } else if (file.name.endsWith(".csv")) {
        // Parse CSV
        try {
          const lines = content.split("\n");
          const newTranslations = {};
          lines.slice(1).forEach(line => {
            const match = line.match(/^"([^"]*)","[^"]*","([^"]*)"$/);
            if (match && match[2]) {
              newTranslations[match[1]] = match[2];
            }
          });
          bulkUpdateTranslations(selectedLanguage, newTranslations);
          toast.success(`Imported ${Object.keys(newTranslations).length} translations`);
        } catch (err) {
          toast.error("Failed to parse CSV file");
        }
      }
      setUploadDialogOpen(false);
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    const rows = [["key", "english", "translation"]];
    allKeys.forEach(key => {
      rows.push([key, enTranslations[key] || "", ""]);
    });
    const content = rows.map(row => row.map(cell => `"${(cell || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([content], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "translation_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded template");
  };

  const selectedLang = SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage);

  return (
    <div data-testid="translation-management-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="translation-title">
          {t("translation.title")}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t("translation.subtitle")}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        {SUPPORTED_LANGUAGES.slice(0, 4).map(lang => {
          const langStats = getTranslationStats(lang.code);
          return (
            <div
              key={lang.code}
              className={`bg-white border rounded-lg p-4 cursor-pointer transition-all ${
                selectedLanguage === lang.code ? "border-cyan-500 ring-2 ring-cyan-100" : "border-slate-200 hover:border-slate-300"
              }`}
              onClick={() => setSelectedLanguage(lang.code)}
              data-testid={`lang-stats-${lang.code}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{FLAG_EMOJIS[lang.flag]}</span>
                <span className="font-medium text-slate-900">{lang.name}</span>
              </div>
              <Progress value={langStats.percentage} className="h-1.5 mb-2" />
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">{langStats.translated}/{langStats.total} keys</span>
                <span className={langStats.percentage === 100 ? "text-emerald-600" : "text-amber-600"}>
                  {langStats.percentage}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Language Selector for all languages */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Editing Language</Label>
              <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <SelectTrigger className="w-48" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <div className="flex items-center gap-2">
                        <span>{FLAG_EMOJIS[lang.flag]}</span>
                        <span>{lang.name}</span>
                        <span className="text-slate-400 text-xs">({lang.nativeName})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="h-10 border-l border-slate-200" />
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span className="text-slate-600">{stats.translated} translated</span>
              <span className="text-slate-300">|</span>
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <span className="text-slate-600">{stats.missing} missing</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setUploadDialogOpen(true)} data-testid="bulk-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> {t("translation.bulk_upload")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport("json")} data-testid="export-json-btn">
              <Download className="w-4 h-4 mr-2" /> Export JSON
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport("csv")}>
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search keys or values..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="translation-search"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="translated">Translated</SelectItem>
              <SelectItem value="missing">Missing</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setAddKeyDialogOpen(true)} data-testid="add-key-btn">
          <Plus className="w-4 h-4 mr-2" /> {t("translation.add_key")}
        </Button>
      </div>

      {/* Translation Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
          <div className="col-span-4">{t("translation.key")}</div>
          <div className="col-span-4">{t("translation.base_value")} (English)</div>
          <div className="col-span-3">
            {selectedLang && (
              <div className="flex items-center gap-1.5">
                <span>{FLAG_EMOJIS[selectedLang.flag]}</span>
                <span>{selectedLang.name}</span>
              </div>
            )}
          </div>
          <div className="col-span-1 text-right">{t("label.actions")}</div>
        </div>
        
        <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto" data-testid="translation-table">
          {Object.entries(groupedKeys).map(([category, keys]) => (
            <div key={category}>
              <div className="px-4 py-2 bg-slate-50/50 text-xs font-semibold text-slate-600 uppercase tracking-wider sticky top-0">
                {category} ({keys.length})
              </div>
              {keys.map(key => {
                const enValue = enTranslations[key] || "";
                const langValue = langTranslations[key] || "";
                const isEditing = editingKey === key;
                const isMissing = !langValue;

                return (
                  <div
                    key={key}
                    className={`grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-50 ${isMissing ? "bg-amber-50/30" : ""}`}
                    data-testid={`translation-row-${key}`}
                  >
                    <div className="col-span-4">
                      <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">{key}</code>
                    </div>
                    <div className="col-span-4 text-sm text-slate-600 truncate" title={enValue}>
                      {enValue}
                    </div>
                    <div className="col-span-3">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            className="h-8 text-sm"
                            autoFocus
                            data-testid={`edit-input-${key}`}
                          />
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleUpdateTranslation(key)}>
                            <Check className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditingKey(null)}>
                            <X className="w-4 h-4 text-slate-400" />
                          </Button>
                        </div>
                      ) : (
                        <div
                          className={`text-sm cursor-pointer px-2 py-1 rounded hover:bg-slate-100 ${isMissing ? "text-amber-600 italic" : "text-slate-900"}`}
                          onClick={() => { setEditingKey(key); setEditingValue(langValue); }}
                          data-testid={`translation-value-${key}`}
                        >
                          {langValue || "Click to add translation..."}
                        </div>
                      )}
                    </div>
                    <div className="col-span-1 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isMissing ? (
                          <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-600 border-amber-200">
                            Missing
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200">
                            <Check className="w-3 h-3 mr-0.5" /> Done
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                          onClick={() => handleDeleteKey(key)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Add Key Dialog */}
      <Dialog open={addKeyDialogOpen} onOpenChange={setAddKeyDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="add-key-desc">
          <DialogHeader>
            <DialogTitle>Add Translation Key</DialogTitle>
            <DialogDescription id="add-key-desc">
              Add a new translation key with its English (base) value
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs">Translation Key</Label>
              <Input
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="e.g., nav.new_feature"
                className="mt-1"
                data-testid="new-key-input"
              />
              <p className="text-xs text-slate-400 mt-1">Use dot notation for categories (nav., action., label.)</p>
            </div>
            <div>
              <Label className="text-xs">English Value</Label>
              <Textarea
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="Enter the English text..."
                className="mt-1"
                rows={2}
                data-testid="new-value-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddKeyDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddKey} disabled={!newKey || !newValue} data-testid="save-key-btn">
              Add Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="upload-desc">
          <DialogHeader>
            <DialogTitle>Bulk Upload Translations</DialogTitle>
            <DialogDescription id="upload-desc">
              Upload a JSON or CSV file to update translations for {selectedLang?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".json,.csv"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
              />
              <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-2">Drop your file here or click to browse</p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <FileJson className="w-4 h-4 mr-2" /> Upload JSON
                </Button>
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Upload CSV
                </Button>
              </div>
            </div>
            <div className="text-center">
              <Button variant="link" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-1" /> Download CSV Template
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
